import { useMemo } from 'react';

import { DualAxisChart } from '@/components/charts/dual-axis-chart';
import { Skeleton } from '@/components/ui/skeleton';
import { formatCurrency, formatNumber } from '@/lib/utils';

import type { HourlyTrafficPoint } from '../dashboard.api';

interface HourlyTrafficChartProps {
  data: HourlyTrafficPoint[] | undefined;
  loading: boolean;
  height?: number;
}

/** Patrón de tráfico por hora: transacciones en barras y monto en línea. */
export function HourlyTrafficChart({ data, loading, height }: HourlyTrafficChartProps) {
  const { labels, transacciones, montos, pico } = useMemo(() => {
    const puntos = data ?? [];

    let pico: HourlyTrafficPoint | null = null;
    for (const punto of puntos) {
      if (!pico || Number(punto.monto || 0) > Number(pico.monto || 0)) pico = punto;
    }

    return {
      labels: puntos.map((punto) => punto.hora_label),
      transacciones: puntos.map((punto) => Number(punto.transacciones || 0)),
      montos: puntos.map((punto) => Number(punto.monto || 0)),
      pico,
    };
  }, [data]);

  if (loading) {
    return <Skeleton className="h-72 rounded-control" />;
  }

  if (labels.length === 0) {
    return (
      <p className="py-8 text-center text-sm text-ink-500">Sin ventas registradas en este rango.</p>
    );
  }

  return (
    <div className="flex h-full flex-col justify-center gap-4">
      <DualAxisChart
        labels={labels}
        series={[
          {
            label: 'Transacciones',
            data: transacciones,
            axis: 'left',
            kind: 'bar',
            color: '#06b6d4',
          },
          {
            label: 'Monto (S/)',
            data: montos,
            axis: 'right',
            kind: 'line',
            color: '#10b981',
          },
        ]}
        leftTitle="Transacciones"
        rightTitle="Monto (S/)"
        leftFormat="number"
        rightFormat="currency"
        height={height}
      />

      {pico && Number(pico.monto || 0) > 0 ? (
        <p className="border-t border-ink-100 pt-3 text-xs text-ink-500">
          Hora de mayor venta:{' '}
          <strong className="font-semibold text-ink-800">{pico.hora_label}</strong> con{' '}
          <strong className="font-semibold text-ink-800">{formatCurrency(pico.monto)}</strong> en{' '}
          {formatNumber(pico.transacciones)} operaciones
          {Number(pico.galones || 0) > 0 ? ` · ${formatNumber(pico.galones)} gal` : ''}.
        </p>
      ) : null}
    </div>
  );
}
